import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Eye, EyeOff, Printer, RotateCcw, Save } from 'lucide-react';
import { ReportHeader } from './reports/layout/ReportHeader';
import { PatientStrip } from './reports/layout/PatientStrip';
import { SignatureFooter } from './reports/layout/SignatureFooter';
import ReportAudiogram from './ReportAudiogram';
import TympanogramCanvas from './TympanogramCanvas';
import { ptaAvg } from './reports/ptaCalc';
import { LABELS, pick } from './reports/constants';
import {
  printReportV2,
  A4_WIDTH_PX,
  A4_HEIGHT_PX,
  A4_PADDING_PX,
} from './reports/printReportV2';

/**
 * Report 2 — a standalone A4 report canvas built from scratch alongside
 * (but never touching) the existing Reports tab.
 *
 * Design goals:
 *   • FIXED A4 print target — canvas is ALWAYS exactly
 *     794 × 1123 px (210 × 297 mm @ 96 dpi). No overflow, no
 *     second page, ever. `overflow: hidden` on the canvas is the
 *     paranoid backstop.
 *   • Stacked components, each with a visible eye-icon toggle:
 *       Header · Patient · Audiogram (square, combined) · Tympanogram
 *       (optional, off by default) · PTA summary · Tuning Fork
 *       · Findings · Signature.
 *   • Combined audiogram is ALWAYS a square. Explicit equal `width` /
 *     `height` pixels (measured via ResizeObserver on the flex-1
 *     container) — NOT `aspect-ratio` CSS which browsers drop in the
 *     `@media print` context.
 *   • A4 auto-fit — fixed sections take their natural height, the
 *     chart region (audiogram + optional tympanogram) shares the
 *     remaining vertical space via `flex: 1`. When both charts are on,
 *     they split remaining height 50 / 50.
 *   • PTA + Tuning Fork render side-by-side (50 / 50) when BOTH are
 *     enabled; either alone renders full width. Tuning Fork is off
 *     by default and populated from `preTestData.tuning_fork`.
 *   • Print / Export PDF button — this component's toolbar AND the
 *     top-of-page Print button in TestProceduresModule both call the
 *     SAME `printReportV2()` helper, so their output is identical.
 *
 * Independence:
 *   • No shared state with `ReportsPanel` — this component reads the
 *     same session props but keeps its own visibility state.
 *   • Print CSS is scoped to `body.printing-report-v2` so opening this
 *     tab NEVER affects the existing Report tab's print behaviour.
 */

// PTA summary — inline, self-contained (kept out of PureToneSection so
// this file has no cross-tab imports).
const PTAMiniTable = ({ rightEar, leftEar }) => {
  const rP1 = ptaAvg(rightEar, 'ac_measurements', [500, 1000, 2000]);
  const rP2 = ptaAvg(rightEar, 'ac_measurements', [1000, 2000, 4000]);
  const lP1 = ptaAvg(leftEar,  'ac_measurements', [500, 1000, 2000]);
  const lP2 = ptaAvg(leftEar,  'ac_measurements', [1000, 2000, 4000]);
  const rB1 = ptaAvg(rightEar, 'bc_measurements', [500, 1000, 2000]);
  const lB1 = ptaAvg(leftEar,  'bc_measurements', [500, 1000, 2000]);
  const rABG = rP1 !== null && rB1 !== null ? rP1 - rB1 : null;
  const lABG = lP1 !== null && lB1 !== null ? lP1 - lB1 : null;
  return (
    <div className="border border-gray-300 rounded bg-white" data-testid="report-v2-pta-summary">
      <div className="text-[11px] font-bold text-gray-800 bg-gray-100 text-center py-1 border-b border-gray-300">
        PTA Summary (dB HL)
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-2 py-1 text-left font-semibold text-gray-600">Ear</th>
            <th className="px-2 py-1 font-semibold text-gray-700" title="Average of 500, 1000, 2000 Hz">PTA 1</th>
            <th className="px-2 py-1 font-semibold text-gray-700" title="Average of 1000, 2000, 4000 Hz">PTA 2</th>
            <th className="px-2 py-1 font-semibold text-gray-700" title="AC PTA1 − BC PTA1 (500·1K·2K Hz)">AB Gap</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-100">
            <td className="px-2 py-1 font-semibold text-red-600">R</td>
            <td className="px-2 py-1 text-center font-mono">{rP1 ?? '—'}</td>
            <td className="px-2 py-1 text-center font-mono">{rP2 ?? '—'}</td>
            <td className="px-2 py-1 text-center font-mono">{rABG ?? '—'}</td>
          </tr>
          <tr>
            <td className="px-2 py-1 font-semibold text-blue-600">L</td>
            <td className="px-2 py-1 text-center font-mono">{lP1 ?? '—'}</td>
            <td className="px-2 py-1 text-center font-mono">{lP2 ?? '—'}</td>
            <td className="px-2 py-1 text-center font-mono">{lABG ?? '—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Tuning Fork mini-table — Rinne · Weber with Right / Left columns.
// Populated from `preTestData.tuning_fork` when available; falls back to blank cells.
const TuningForkMiniTable = ({ tf = {} }) => {
  const weberRight =
    tf.weber === 'right' ? 'Louder' :
    tf.weber === 'midline' ? 'Midline' : '';
  const weberLeft =
    tf.weber === 'left' ? 'Louder' :
    tf.weber === 'midline' ? 'Midline' : '';
  const rinneRight = tf.rinne_right ? pick(LABELS.rinne, tf.rinne_right) : '';
  const rinneLeft  = tf.rinne_left  ? pick(LABELS.rinne, tf.rinne_left)  : '';
  const rows = [
    { id: 'rinne', label: 'Rinne', r: rinneRight, l: rinneLeft },
    { id: 'weber', label: 'Weber', r: weberRight, l: weberLeft },
  ];
  return (
    <div className="border border-gray-300 rounded bg-white" data-testid="report-v2-tuning-fork">
      <div className="text-[11px] font-bold text-gray-800 bg-gray-100 text-center py-1 border-b border-gray-300">
        Tuning Fork ({tf.frequency_hz || 512} Hz)
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-2 py-1 text-left font-semibold text-gray-600">Test</th>
            <th className="px-2 py-1 font-semibold text-red-600">Right</th>
            <th className="px-2 py-1 font-semibold text-blue-600">Left</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.id}
              className={idx < rows.length - 1 ? 'border-b border-gray-100' : ''}
              data-testid={`report-v2-tf-row-${row.id}`}
            >
              <td className="px-2 py-1 font-semibold text-gray-800">{row.label}</td>
              <td className="px-2 py-1 text-center font-mono">{row.r || '—'}</td>
              <td className="px-2 py-1 text-center font-mono">{row.l || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Eye-icon toggle rendered right of each section title. Kept small so it
// doesn't overpower the printable content it gates.
const SectionToggle = ({ visible, onToggle, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={visible}
    data-testid={testid}
    title={visible ? 'Hide from print' : 'Show in print'}
    className="p-1 rounded hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition-colors no-print"
  >
    {visible ? <Eye size={14} /> : <EyeOff size={14} />}
  </button>
);

// Section wrapper. Supports:
//   * `grow`  — apply `flex: 1, minHeight: 0` when visible, so the
//     audiogram / tympanogram rows can share the remaining vertical
//     space inside the fixed-height A4 canvas.
//   * hidden state — shows a compact "hidden, click to show" affordance
//     that never prints.
const Section = ({ id, label, visible, onToggle, children, grow }) => {
  if (!visible) {
    return (
      <div className="no-print border border-dashed border-slate-300 rounded px-3 py-1 text-[11px] text-slate-500 flex items-center justify-between">
        <span>{label} — hidden</span>
        <SectionToggle
          visible={false}
          onToggle={onToggle}
          testid={`report-v2-toggle-${id}`}
        />
      </div>
    );
  }
  const growStyle = grow ? { flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined;
  return (
    <section
      data-testid={`report-v2-section-${id}`}
      className="relative"
      style={growStyle}
    >
      <div className="absolute -top-1 -right-1 no-print z-10">
        <SectionToggle
          visible
          onToggle={onToggle}
          testid={`report-v2-toggle-${id}`}
        />
      </div>
      {children}
    </section>
  );
};

// Square audiogram — takes 100 % of a `flex: 1` container and paints
// a CENTERED square of size `min(container.width, container.height)`.
// Explicit pixel dims (NOT aspect-ratio) survive the `@media print`
// pass; the chart is guaranteed square in both preview and paper.
const SquareAudiogram = ({ rightEar, leftEar }) => {
  const wrapRef = useRef(null);
  const [size, setSize] = useState(400);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const s = Math.max(0, Math.floor(Math.min(r.width, r.height)));
      setSize((prev) => (Math.abs(prev - s) > 0.5 ? s : prev));
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    return undefined;
  }, []);

  return (
    <div
      ref={wrapRef}
      style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <div
        data-testid="report-v2-audiogram"
        style={{ width: `${size}px`, height: `${size}px` }}
      >
        <ReportAudiogram rightEarData={rightEar} leftEarData={leftEar} />
      </div>
    </div>
  );
};

// Optional Tympanogram panel — two SVG plots (Right + Left) side-by-side.
// Reads `impedanceData.tympanometry.{right,left}`; falls back to a
// placeholder curve if numerical values are absent.
const TympanogramPanel = ({ impedanceData }) => {
  const tymp = impedanceData?.tympanometry || {};
  const R = tymp.right || {};
  const L = tymp.left  || {};
  return (
    <div
      data-testid="report-v2-tympanogram"
      className="w-full h-full grid grid-cols-2 gap-2 border border-gray-300 rounded bg-white p-1"
    >
      <div className="flex flex-col min-h-0">
        <div className="text-[10px] font-bold text-red-600 text-center border-b border-gray-200 pb-0.5 mb-0.5">
          Right — Tympanogram
        </div>
        <div className="flex-1 min-h-0">
          <TympanogramCanvas
            jergerType={R.jerger_type}
            mePressure={R.me_pressure}
            compliance={R.compliance}
            earSide="right"
            probeHz={R.probe_hz || 226}
          />
        </div>
      </div>
      <div className="flex flex-col min-h-0">
        <div className="text-[10px] font-bold text-blue-600 text-center border-b border-gray-200 pb-0.5 mb-0.5">
          Left — Tympanogram
        </div>
        <div className="flex-1 min-h-0">
          <TympanogramCanvas
            jergerType={L.jerger_type}
            mePressure={L.me_pressure}
            compliance={L.compliance}
            earSide="left"
            probeHz={L.probe_hz || 226}
          />
        </div>
      </div>
    </div>
  );
};

// Persist the section-visibility map across tab switches / remounts.
// Scoped per browser (localStorage) — a single Report 2 layout is fine
// since visibility is a UI preference, not per-session clinical data.
const VISIBILITY_STORAGE_KEY = 'audinexa_report_v2_visibility_v1';
const DEFAULT_VISIBILITY = {
  header: true,
  patient: true,
  audiogram: true,
  tympanogram: false,
  pta: true,
  tuningfork: false,
  findings: true,
  signature: true,
};

const loadVisibility = () => {
  if (typeof window === 'undefined') return DEFAULT_VISIBILITY;
  try {
    const raw = window.localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBILITY;
    const parsed = JSON.parse(raw);
    // Merge over defaults so newly-added section keys pick up their
    // default value on first render after a code change.
    return { ...DEFAULT_VISIBILITY, ...(parsed && typeof parsed === 'object' ? parsed : {}) };
  } catch {
    return DEFAULT_VISIBILITY;
  }
};

const saveVisibility = (v) => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(v)); } catch { /* quota / private-mode — silently drop */ }
};

// Named-layout presets — clinicians can flip between three common
// report shapes with a single click. Each preset is a full visibility
// map; loading it OVERWRITES the current state (which is also mirrored
// to localStorage via the existing effect, so the change survives a
// tab switch or refresh). Save-current-as writes back to a slot so the
// preset itself is user-editable — the defaults are just seed values
// on first mount.
const PRESET_STORAGE_KEY = 'audinexa_report_v2_presets_v1';
const PRESET_KEYS = ['screening', 'diagnostic', 'followup'];
const PRESET_LABELS = {
  screening:  'Screening',
  diagnostic: 'Diagnostic',
  followup:   'Follow-up',
};
const DEFAULT_PRESETS = {
  // Screening — quickest possible report: chart + one-line findings.
  screening:  { header: true, patient: true, audiogram: true,  tympanogram: false, pta: false, tuningfork: false, findings: true, signature: true },
  // Diagnostic — the full battery: both charts and both tables.
  diagnostic: { header: true, patient: true, audiogram: true,  tympanogram: true,  pta: true,  tuningfork: true,  findings: true, signature: true },
  // Follow-up — same audiogram + PTA comparison for progress visits.
  followup:   { header: true, patient: true, audiogram: true,  tympanogram: false, pta: true,  tuningfork: false, findings: true, signature: true },
};

const loadPresets = () => {
  if (typeof window === 'undefined') return { ...DEFAULT_PRESETS };
  try {
    const raw = window.localStorage.getItem(PRESET_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PRESETS };
    const parsed = JSON.parse(raw);
    // Merge over defaults so future default changes / new preset keys
    // pick up sane seed values on next mount.
    const out = { ...DEFAULT_PRESETS };
    for (const k of PRESET_KEYS) {
      if (parsed && parsed[k] && typeof parsed[k] === 'object') {
        out[k] = { ...DEFAULT_VISIBILITY, ...parsed[k] };
      }
    }
    return out;
  } catch {
    return { ...DEFAULT_PRESETS };
  }
};

const savePresets = (p) => {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(p)); } catch { /* best-effort */ }
};

const ReportV2Panel = ({
  patient,
  rightEarData,
  leftEarData,
  preTestData,
  impedanceData,
  audiologistName,
  audiologistUserId,
  clinic,
  findingsText = '',
  onPersistFindings,
}) => {
  const [visible, setVisible] = useState(loadVisibility);
  useEffect(() => { saveVisibility(visible); }, [visible]);
  const [presets, setPresets] = useState(loadPresets);
  useEffect(() => { savePresets(presets); }, [presets]);
  const [localFindings, setLocalFindings] = useState(findingsText);
  useEffect(() => { setLocalFindings(findingsText); }, [findingsText]);

  const toggle = useCallback(
    (id) => setVisible((v) => ({ ...v, [id]: !v[id] })),
    [],
  );

  // Wipe the clinician's persisted layout and jump every section back to
  // its factory-default visibility. localStorage is cleared FIRST so a
  // remount can't rehydrate a stale value between the state update and
  // the useEffect that mirrors state → storage.
  const resetLayout = useCallback(() => {
    try { window.localStorage.removeItem(VISIBILITY_STORAGE_KEY); } catch { /* private-mode / quota — best-effort */ }
    // Fresh object — never share a reference with DEFAULT_VISIBILITY so
    // future toggles can't mutate the module-level default.
    setVisible({ ...DEFAULT_VISIBILITY });
  }, []);

  // Load a named preset — overwrites the current visibility map.
  // Guarded against unknown keys (defensive; the buttons only pass
  // whitelisted values but a future callsite might not).
  const loadPreset = useCallback((key) => {
    if (!PRESET_KEYS.includes(key)) return;
    setVisible({ ...DEFAULT_VISIBILITY, ...(presets[key] || {}) });
  }, [presets]);

  // Save the current visibility map INTO a named preset slot. Uses a
  // native prompt so we avoid pulling in a modal library just for this.
  const saveCurrentAsPreset = useCallback(() => {
    const raw = window.prompt(
      `Save the current layout as which preset?\n\n` +
      `Type: screening, diagnostic, or followup\n` +
      `(This overwrites the preset — no undo.)`,
      'screening',
    );
    if (!raw) return;
    const key = raw.trim().toLowerCase();
    if (!PRESET_KEYS.includes(key)) { window.alert('Enter screening, diagnostic, or followup.'); return; }
    setPresets((prev) => ({ ...prev, [key]: { ...visible } }));
    window.alert(`✅ Saved current layout as "${PRESET_LABELS[key]}".`);
  }, [visible]);

  return (
    <div className="flex-1 overflow-auto bg-gray-200 p-4" data-testid="report-v2-panel">
      {/* Toolbar — not printed. */}
      <div
        className="mb-3 flex items-center justify-between gap-3 bg-white border border-slate-200 rounded shadow-sm px-3 py-2 no-print flex-wrap"
        style={{ width: `${A4_WIDTH_PX}px`, marginLeft: 'auto', marginRight: 'auto' }}
      >
        <div className="text-xs font-semibold text-slate-700 shrink-0">
          Report 2 · A4 · {A4_WIDTH_PX}×{A4_HEIGHT_PX}px
        </div>
        {/* Preset chips — one-click layout switch. */}
        <div className="flex items-center gap-1 shrink-0" data-testid="report-v2-preset-bar">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Preset</span>
          {PRESET_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => loadPreset(key)}
              data-testid={`report-v2-preset-${key}`}
              title={`Load the "${PRESET_LABELS[key]}" layout`}
              className="px-2 py-0.5 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-colors"
            >
              {PRESET_LABELS[key]}
            </button>
          ))}
          <button
            type="button"
            onClick={saveCurrentAsPreset}
            data-testid="report-v2-preset-save"
            title="Save the current section toggles into a preset slot"
            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-transparent rounded transition-colors"
          >
            <Save size={12} /> Save…
          </button>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={resetLayout}
            data-testid="report-v2-reset-layout"
            title="Restore every section toggle to its default (Audiogram · PTA · Findings · Signature on; Tympanogram · Tuning Fork off)"
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
          >
            <RotateCcw size={12} /> Reset layout
          </button>
          <button
            type="button"
            onClick={printReportV2}
            data-testid="report-v2-print"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-slate-800 hover:bg-slate-900 text-white rounded shadow-sm"
          >
            <Printer size={14} /> Print / Export PDF
          </button>
        </div>
      </div>

      {/* A4 canvas — FIXED pixel dims + overflow:hidden so nothing ever
          spills to a second page. Same dims live in App.css for
          `body.printing-report-v2` so preview and paper match. */}
      <div
        id="report-v2-canvas"
        data-testid="report-v2-canvas"
        className="bg-white shadow-lg"
        style={{
          width: `${A4_WIDTH_PX}px`,
          height: `${A4_HEIGHT_PX}px`,
          padding: `${A4_PADDING_PX}px`,
          marginLeft: 'auto',
          marginRight: 'auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          fontFamily: clinic?.report_font || 'Arial, sans-serif',
          color: '#1f2937',
          boxSizing: 'border-box',
        }}
      >
        <Section id="header" label="Header" visible={visible.header} onToggle={() => toggle('header')}>
          <ReportHeader clinic={clinic} />
        </Section>

        <Section id="patient" label="Patient info" visible={visible.patient} onToggle={() => toggle('patient')}>
          <PatientStrip
            patient={patient}
            referredBy={patient?.referred_by || null}
            audiologistName={audiologistName}
          />
        </Section>

        {/* Chart region — Audiogram + optional Tympanogram share
            `flex: 1 1 0`. When both on → 50/50 split. When only one on →
            it takes the full remaining space. When neither → no
            grow, the fixed rows below just stack from the top. */}
        <Section id="audiogram" label="Audiogram" visible={visible.audiogram} onToggle={() => toggle('audiogram')} grow>
          <SquareAudiogram rightEar={rightEarData} leftEar={leftEarData} />
        </Section>

        <Section id="tympanogram" label="Tympanogram" visible={visible.tympanogram} onToggle={() => toggle('tympanogram')} grow>
          <TympanogramPanel impedanceData={impedanceData} />
        </Section>

        {/* PTA + Tuning Fork row:
            — both visible  →  side-by-side 50 / 50
            — only PTA      →  PTA full width
            — only TF       →  Tuning Fork full width
            — neither       →  each renders its own hidden-affordance row */}
        {visible.pta && visible.tuningfork ? (
          <div className="grid grid-cols-2 gap-3" data-testid="report-v2-pta-tf-row">
            <Section id="pta" label="PTA summary" visible={true} onToggle={() => toggle('pta')}>
              <PTAMiniTable rightEar={rightEarData} leftEar={leftEarData} />
            </Section>
            <Section id="tuningfork" label="Tuning Fork" visible={true} onToggle={() => toggle('tuningfork')}>
              <TuningForkMiniTable tf={preTestData?.tuning_fork} />
            </Section>
          </div>
        ) : (
          <>
            <Section id="pta" label="PTA summary" visible={visible.pta} onToggle={() => toggle('pta')}>
              <PTAMiniTable rightEar={rightEarData} leftEar={leftEarData} />
            </Section>
            <Section id="tuningfork" label="Tuning Fork" visible={visible.tuningfork} onToggle={() => toggle('tuningfork')}>
              <TuningForkMiniTable tf={preTestData?.tuning_fork} />
            </Section>
          </>
        )}

        <Section id="findings" label="Results / Findings" visible={visible.findings} onToggle={() => toggle('findings')}>
          <div>
            <div className="text-[11px] font-bold text-blue-700 bg-blue-50 border-l-2 border-blue-600 px-2 py-1 mb-1">
              Results / Findings
            </div>
            <textarea
              value={localFindings}
              onChange={(e) => setLocalFindings(e.target.value)}
              onBlur={() => onPersistFindings?.(localFindings)}
              placeholder="Enter findings, interpretation, or clinical impression…"
              rows={4}
              className="w-full text-[11px] px-2 py-1 border border-slate-300 rounded resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 print:border-transparent"
              data-testid="report-v2-findings"
            />
          </div>
        </Section>

        <Section id="signature" label="Signature block" visible={visible.signature} onToggle={() => toggle('signature')}>
          <SignatureFooter
            audiologistName={audiologistName}
            audiologistUserId={audiologistUserId}
          />
        </Section>
      </div>
    </div>
  );
};

export default ReportV2Panel;
